import React from "react";
import { Skeleton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import TopBarRow from "./TopBarRow";
import TopBarDivider from "./TopBarDivider";
import { tilesInOneRow, useTopBarTileSize } from "./TopBarTile";

// The standard template fills two rows, so that is the height reserved while the fields are still unknown.
const tilesWhileLoading = tilesInOneRow * 2;

export interface OverviewTileRowsProps {
    // One ordered sequence, in the order the tiles should read. Falsy entries are dropped so a caller can pass a tile
    // that is behind an access check without it taking up a place in a row.
    tiles: Array<React.ReactNode>;
    // The fields that decide which tiles exist arrive with the data itself, so until then the tiles cannot draw their
    // own skeletons and placeholders stand in for all of them.
    isLoading?: boolean;
}

// Rows are where the sequence wraps, not groups with meanings of their own. Filling each row before starting the next
// keeps a template that drops fields from leaving a short row sitting above a full one.
function OverviewTileRows(props: OverviewTileRowsProps) {
    const tiles = props.isLoading ? Array.from({ length: tilesWhileLoading }, (_, index) => <LoadingTile key={index} />) : props.tiles.filter(Boolean);
    const rows: Array<Array<React.ReactNode>> = [];

    for (let index = 0; index < tiles.length; index += tilesInOneRow) {
        rows.push(tiles.slice(index, index + tilesInOneRow));
    }

    return (
        <>
            {rows.map((row, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <TopBarDivider />}
                    <TopBarRow>{row}</TopBarRow>
                </React.Fragment>
            ))}
        </>
    );
}

function LoadingTile() {
    const theme = useTheme();
    const size = useTopBarTileSize();
    return (
        <Grid size={size}>
            <Skeleton variant="text" sx={{ marginX: "auto", width: theme.spacing(12) }} />
            <Skeleton variant="text" sx={{ marginX: "auto", width: theme.spacing(8) }} />
        </Grid>
    );
}

export default OverviewTileRows;
